const normalise = value => String(value).normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();

export function parseSearchIntent(query) {
  const text = normalise(query);
  if (!text || text.length > 160 || /[:"()\\*]/u.test(text) || /\b(?:ref|referencia|reference|codigo|code|modelo|model)\b/.test(text)) return null;
  if (/\d\s*[-/]\s*\d/.test(text)) return null;
  const numbers = text.match(/\d+(?:[.,]\d+)?/g) || [];
  if (numbers.length !== 1) return null;
  const sizeMatch = text.match(/\b(?:talla|numero|size)\s*(?:de\s*)?(\d{1,2}(?:[.,]\d)?)(?:\s*(anos?|mes(?:es)?|years?|months?))?\b/) || text.match(/\b(\d{1,2})\s*(anos?|mes(?:es)?|years?|months?)\b/);
  if (!sizeMatch) return null;
  const number = sizeMatch[1].replace(',', '.');
  const units = sizeMatch[2] || '';
  const unit = /ano|year/.test(units) ? 'years' : /mes|month/.test(units) ? 'months' : null;
  const girl = /\b(?:ninas?|girls?)\b/.test(text);
  const boy = /\b(?:ninos?|boys?)\b/.test(text);
  const baby = /\b(?:bebe|bebes|baby|babies)\b/.test(text);
  if ([girl, boy, baby].filter(Boolean).length > 1) return null;
  const gender = girl ? 'girl' : boy ? 'boy' : baby ? 'baby' : null;
  let key = null;
  let kind = 'fashion';
  const shoes = [
    [/\b(?:botas? de agua|wellies|rain boots?)\b/, 'botas-agua'],
    [/\b(?:zapatillas? de casa|slippers?)\b/, 'casa'],
    [/\b(?:merceditas?|bailarinas?|mary janes?)\b/, 'merceditas-y-bailarinas'],
    [/\b(?:deportiv[ao]s?|zapatillas?|trainers?|sneakers?)\b/, 'deportivas'],
    [/\b(?:botas?|botines?|boots?)\b/, 'botas-y-botines'],
    [/\b(?:sandalias?|menorquinas?|sandals?)\b/, 'sandalias-y-menorquinas'],
    [/\b(?:lonas?|canvas)\b/, 'lonas'],
    [/\b(?:colegiales|school shoes?)\b/, 'colegiales'],
    [/\b(?:pepitos?|bluchers?|inglesitos?)\b/, 'pepitos-bluchers-e-inglesitos'],
    [/\b(?:calzado|zapatos?|shoes?|footwear)\b/, 'calzado'],
  ];
  for (const [pattern, destination] of shoes) {
    if (pattern.test(text)) { key = destination; kind = 'shoes'; break; }
  }
  if (kind === 'shoes' && /\b(?:vestidos?|jesusitos?|conjuntos?|abrigos?|peleles?|camisas?|pantalones|dress|dresses|sets?|coats?|rompers?|shirts?|trousers)\b/.test(text)) return null;
  if (kind === 'shoes' && unit) return null;
  if (!key) {
    if (/\b(?:vestidos?|jesusitos?|dresses|dress)\b/.test(text)) {
      if (boy) return null;
      key = baby ? 'vestidos-y-jesusitos-bebe' : 'vestidos-y-jesusitos';
    } else if (/\b(?:conjuntos?|sets?)\b/.test(text)) {
      key = baby ? 'conjuntos-bebe' : girl ? 'conjuntos-nina' : boy ? 'conjuntos-nino' : null;
    } else if (/\b(?:abrigos?|coats?)\b/.test(text)) {
      key = baby ? 'abrigos-y-chaquetas-bebe' : girl ? 'abrigos-y-chaquetas-nina' : boy ? 'abrigos' : null;
    } else if (/\b(?:peleles?|rompers?)\b/.test(text)) {
      key = 'peleles';
    } else if (/\b(?:camisas?|shirts?)\b/.test(text) && boy) {
      key = 'camisas';
    } else if (/\b(?:pantalones|trousers)\b/.test(text) && boy) {
      key = 'pantalones';
    } else {
      const remaining = text.replace(/\b(?:ropa|clothes|clothing|ninas?|girls?|ninos?|boys?|bebe|bebes|baby|babies|talla|numero|size|de|para|for|en|anos?|mes(?:es)?|years?|months?)\b/g, '').replace(/[\d.,\s]/g, '');
      if (remaining) return null;
      key = baby ? 'bebe' : girl ? 'nina' : boy ? 'nino' : null;
    }
  }
  if (!key) return null;
  return { key, kind, gender, number, unit };
}

export function matchingSizeValues(filter, intent) {
  if (!filter || filter.role !== intent.kind) return [];
  return filter.values.filter(value => {
    if (!(value.count > 0)) return false;
    const label = normalise(value.value);
    if (intent.kind === 'shoes') return label.replace(',', '.') === intent.number;
    const parsed = /^(\d{1,2})\s*(anos?|mes(?:es)?|years?|months?)$/.exec(label);
    if (!parsed || parsed[1] !== intent.number) return false;
    const unit = /ano|year/.test(parsed[2]) ? 'years' : 'months';
    return !intent.unit || intent.unit === unit;
  }).slice(0, 2);
}

export function girlTypeValues(filter) {
  // Translated product-type labels are not reliable filter values in every market.
  return (filter?.values || []).filter(value => value.count > 0 && normalise(value.value) === 'zapato nina');
}

const cache = new Map();
async function collectionOptions(destination) {
  const endpoint = new URL(destination);
  endpoint.searchParams.set('section_id', 'bp-search-intent-options');
  const key = endpoint.toString();
  if (!cache.has(key)) {
    cache.set(key, fetch(endpoint, { headers: { Accept: 'text/html' }, signal: AbortSignal.timeout(8000) }).then(async response => {
      if (!response.ok) throw new Error('collection');
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const root = doc.querySelector('[data-bp-intent-options]');
      if (!root || !root.dataset.url) throw new Error('collection');
      return {
        count: Number(root.dataset.count),
        title: root.dataset.title,
        filters: Array.from(root.querySelectorAll('[data-role]')).map(filter => ({
          role: filter.dataset.role,
          param: filter.dataset.param,
          values: Array.from(filter.querySelectorAll('[data-value]')).map(value => ({ value: value.dataset.value, label: value.dataset.label, count: Number(value.dataset.count), active: value.dataset.active === 'true' })),
        })),
      };
    }).catch(error => { cache.delete(key); throw error; }));
  }
  return cache.get(key);
}

if (typeof customElements !== 'undefined' && !customElements.get('bp-search-intent')) {
  customElements.define('bp-search-intent', class extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.showIntent();
    }

    async showIntent() {
      const intent = parseSearchIntent(this.dataset.query || '');
      if (!intent) return;
      const route = Array.from(this.querySelectorAll('[data-key]')).find(link => link.dataset.key === intent.key);
      if (!route) return;
      const target = new URL(route.href, location.origin);
      if (target.origin !== location.origin || !/\/(?:collections)\/[^/]+$/.test(target.pathname)) return;
      const strings = JSON.parse(this.querySelector('[data-intent-strings]').content.textContent);
      const status = this.querySelector('[data-intent-status]');
      const actions = this.querySelector('[data-intent-actions]');
      const note = this.querySelector('[data-intent-note]');
      this.hidden = false;
      status.textContent = strings.loading;
      try {
        const base = await collectionOptions(target);
        if (!this.isConnected) return;
        const availability = base.filters.find(filter => filter.role === 'availability');
        if (!availability?.values.some(value => value.value === '1')) throw new Error('availability');
        target.searchParams.set(availability.param, '1');
        let label = route.textContent.trim();
        let girlFilter = null;
        if (intent.kind === 'shoes' && intent.gender === 'girl' && intent.key === 'calzado') {
          const types = base.filters.find(filter => filter.role === 'type');
          const values = girlTypeValues(types);
          if (values.length) {
            target.searchParams.set(types.param, values.map(value => value.value).join(','));
            girlFilter = { param: types.param, values: values.map(value => value.value) };
            label = strings.girl_shoes;
          }
        }
        const scoped = await collectionOptions(target);
        if (!this.isConnected) return;
        const sizes = scoped.filters.find(filter => filter.role === intent.kind);
        const candidates = matchingSizeValues(sizes, intent);
        const matches = await Promise.all(candidates.map(async size => {
          const url = new URL(target);
          url.searchParams.set(sizes.param, size.value);
          const result = await collectionOptions(url);
          const active = result.filters.find(filter => filter.param === sizes.param)?.values.some(value => value.value === size.value && value.active);
          const available = result.filters.find(filter => filter.param === availability.param)?.values.some(value => value.value === '1' && value.active);
          const matchesGirl = !girlFilter || girlFilter.values.every(selected => result.filters.find(filter => filter.param === girlFilter.param)?.values.some(value => value.value === selected && value.active));
          return result.count > 0 && active && available && matchesGirl ? { url, size } : null;
        }));
        if (!this.isConnected) return;
        status.textContent = '';
        const verified = matches.filter(Boolean);
        for (const { url, size } of verified) {
          const link = document.createElement('a');
          link.href = url.pathname + url.search;
          let sizeLabel = strings.shoe_size.replace('__BP_SIZE__', intent.number);
          if (intent.kind === 'fashion') {
            const unit = /ano|year/.test(normalise(size.value)) ? 'years' : 'months';
            const key = intent.number === '1' ? unit === 'years' ? 'year' : 'month' : unit;
            sizeLabel = strings[key].replace('__BP_SIZE__', intent.number);
          }
          const text = document.createElement('span');
          text.textContent = label + ' · ' + sizeLabel;
          const arrow = document.createElement('span');
          arrow.textContent = '→';
          arrow.setAttribute('aria-hidden', 'true');
          link.append(text, arrow);
          actions.append(link);
        }
        if (verified.length) {
          this.dataset.hasMatch = 'true';
          status.textContent = strings.stock;
          note.textContent = intent.kind === 'fashion' && !intent.unit ? strings.age : intent.kind === 'shoes' && intent.gender && !girlFilter ? strings.mixed : strings.note;
        } else {
          status.textContent = strings.missing;
          const link = document.createElement('a');
          link.href = target.pathname + target.search;
          link.textContent = strings.other + ' · ' + label;
          actions.append(link);
        }
      } catch {
        if (this.isConnected) this.hidden = true;
      }
    }
  });
}
